import { useQuery } from '@tanstack/react-query';
import { collection, getDocs } from 'firebase/firestore';

import { db } from '@/config/firebase';

export interface CapabilityCatalog {
  resourceTypeMap: ReadonlyMap<string, string>;
  technologyMap: ReadonlyMap<string, string>;
  seniorityMap: ReadonlyMap<string, string>;
}

export const capabilityCatalogQueryKeys = {
  all: ['capability-catalog'] as const,
};

export const EMPTY_CAPABILITY_CATALOG: CapabilityCatalog = {
  resourceTypeMap: new Map<string, string>(),
  technologyMap: new Map<string, string>(),
  seniorityMap: new Map<string, string>(),
};

const readName = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

export async function fetchCapabilityCatalog(): Promise<CapabilityCatalog> {
  const [resourceTypesSnapshot, technologiesSnapshot, senioritiesSnapshot] = await Promise.all([
    getDocs(collection(db, 'resourceTypes')),
    getDocs(collection(db, 'technologies')),
    getDocs(collection(db, 'seniorities')),
  ]);

  const resourceTypeMap = new Map<string, string>();
  resourceTypesSnapshot.forEach((docSnapshot) => {
    const name = readName(docSnapshot.data()?.name);
    if (name) {
      resourceTypeMap.set(docSnapshot.id, name);
    }
  });

  const technologyMap = new Map<string, string>();
  technologiesSnapshot.forEach((docSnapshot) => {
    const name = readName(docSnapshot.data()?.name);
    if (name) {
      technologyMap.set(docSnapshot.id, name);
    }
  });

  const seniorityMap = new Map<string, string>();
  senioritiesSnapshot.forEach((docSnapshot) => {
    const name = readName(docSnapshot.data()?.name);
    if (name) {
      seniorityMap.set(docSnapshot.id, name);
    }
  });

  return {
    resourceTypeMap,
    technologyMap,
    seniorityMap,
  };
}

export function useCapabilityCatalogQuery(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};

  return useQuery<CapabilityCatalog>({
    queryKey: capabilityCatalogQueryKeys.all,
    queryFn: fetchCapabilityCatalog,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
  });
}
